import { v4 } from 'uuid';
import {
    AnnotationFrameChange,
    AnnotationPlane,
    AnnotationRecord,
} from '~/domain/annotation/annotation';
import {
    euclideanDistanceMm,
    PointMm,
} from '~/domain/geometry/patientGeometry';

/**
 * Endpoints closer than this (mm) would make a degenerate profile with no
 * intensity to plot, so placing or dragging one is refused. Sub-pixel by
 * orders of magnitude, so it never rejects a legitimately short profile.
 */
export const MIN_PROFILE_LENGTH_MM = 1e-3;

/**
 * A committed line profile. Geometry only, in patient-frame mm; every value is
 * sampled on demand, never cached here.
 */
export interface LineProfile extends AnnotationRecord {
    readonly startMm: PointMm;
    readonly endMm: PointMm;
}

/** An in-progress placement; `endMm` is null until the cursor first moves. */
export interface LineProfileDraft {
    readonly plane: AnnotationPlane;
    readonly startMm: PointMm;
    readonly endMm: PointMm | null;
}

/** `profile`, placed or in progress, with its plane and endpoints moved by `change`. */
export function lineProfileInFrame<T extends LineProfile | LineProfileDraft>(
    profile: T,
    change: AnnotationFrameChange
): T {
    return {
        ...profile,
        plane: change.plane(profile.plane),
        startMm: change.point(profile.startMm),
        endMm: profile.endMm && change.point(profile.endMm),
    };
}

/** Collision-free id. `uuid`, not `crypto`, which is banned repo-wide. */
export function createLineProfileId(): string {
    return `profile-${v4()}`;
}

/** Which end of a profile an edit moves. */
export type ProfileEndpoint = 'start' | 'end';

/**
 * The profile with one endpoint moved, or unchanged when the move would
 * collapse it: a placed profile always has extent, so there is always
 * intensity to plot.
 *
 * Not shared with `updateMeasurementPoint` despite the shape: the rejection
 * rules differ, so one function would take the rule as a parameter and say
 * nothing itself.
 */
export function withMovedEndpoint(
    profile: LineProfile,
    endpoint: ProfileEndpoint,
    pointMm: PointMm
): LineProfile {
    const moved =
        endpoint === 'start'
            ? { ...profile, startMm: pointMm }
            : { ...profile, endMm: pointMm };
    return euclideanDistanceMm(moved.startMm, moved.endMm) <
        MIN_PROFILE_LENGTH_MM
        ? profile
        : moved;
}
